'use client';

import React, { useState } from 'react';
import { Search, Bell, Menu, X, Wifi } from 'lucide-react';
import AppLogo from '@/components/ui/AppLogo';

interface TopbarProps {
  pageTitle: string;
  pageSubtitle?: string;
  onMenuToggle: () => void;
}

export default function Topbar({ pageTitle, pageSubtitle, onMenuToggle }: TopbarProps) {
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  return (
    <header className="sticky top-0 z-30 bg-card/90 backdrop-blur-md border-b border-border h-14 flex items-center px-4 gap-3">
      {/* Mobile logo + menu */}
      <div className="flex items-center gap-2 lg:hidden">
        <button
          onClick={onMenuToggle}
          className="btn-ghost p-2 -ml-2"
          aria-label="Toggle menu"
        >
          <Menu size={20} />
        </button>
        <div className="flex items-center gap-2">
          <AppLogo size={24} />
          <span className="text-sm font-bold text-foreground">MarketLot</span>
        </div>
      </div>

      {/* Page title — desktop */}
      <div className="hidden lg:block flex-1">
        <h1 className="text-base font-semibold text-foreground">{pageTitle}</h1>
        {pageSubtitle && (
          <p className="text-xs text-muted-foreground">{pageSubtitle}</p>
        )}
      </div>

      {/* Search */}
      <div className="flex-1 lg:max-w-xs">
        {searchOpen ? (
          <div className="flex items-center gap-2 bg-input border border-border rounded-xl px-3 py-2">
            <Search size={14} className="text-muted-foreground flex-shrink-0" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search lot, buyer, UTR..."
              className="flex-1 bg-transparent text-sm text-foreground placeholder:text-muted-foreground outline-none"
              autoFocus
            />
            <button onClick={() => { setSearchOpen(false); setSearchQuery(''); }}>
              <X size={14} className="text-muted-foreground" />
            </button>
          </div>
        ) : (
          <button
            onClick={() => setSearchOpen(true)}
            className="flex items-center gap-2 bg-input border border-border rounded-xl px-3 py-2 w-full text-left hover:border-ring transition-colors"
          >
            <Search size={14} className="text-muted-foreground" />
            <span className="text-sm text-muted-foreground flex-1">Search...</span>
            <kbd className="hidden sm:inline text-2xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded font-mono">⌘K</kbd>
          </button>
        )}
      </div>

      {/* Right actions */}
      <div className="flex items-center gap-1">
        {/* Online indicator */}
        <div className="flex items-center gap-1 px-2 py-1 bg-accent/10 rounded-lg">
          <Wifi size={12} className="text-accent" />
          <span className="text-2xs text-accent font-medium hidden sm:inline">Online</span>
        </div>

        {/* Notifications */}
        <button className="btn-ghost p-2 relative">
          <Bell size={18} />
          <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-primary rounded-full" />
        </button>
      </div>
    </header>
  );
}